import { useUserOnboarding } from "@/api/onboarding";
import { useOrgIdFromToken } from "@/hooks/useOrgIdFromToken";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

interface WizardStepInfo {
  id: string;
  type: string;
}

interface OnboardingContextValue {
  currentStep: 1 | 2 | 3 | 4;
  setCurrentStep: (step: 1 | 2 | 3 | 4) => void;
  wizardSteps: WizardStepInfo[];
  setWizardSteps: (steps: WizardStepInfo[]) => void;
  currentWizardStepIndex: number;
  setCurrentWizardStepIndex: (index: number) => void;
  showProgress: boolean;
  setShowProgress: (show: boolean) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function useOnboardingContext() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error(
      "useOnboardingContext must be used within OnboardingProvider",
    );
  }
  return context;
}

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const orgId = useOrgIdFromToken();

  useUserOnboarding({
    enabled: !!orgId,
  });

  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  const [wizardSteps, setWizardSteps] = useState<WizardStepInfo[]>([]);
  const [currentWizardStepIndex, setCurrentWizardStepIndex] = useState(0);
  const [showProgress, setShowProgress] = useState(true);

  return (
    <OnboardingContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        wizardSteps,
        setWizardSteps,
        currentWizardStepIndex,
        setCurrentWizardStepIndex,
        showProgress,
        setShowProgress,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

/**
 * Hook to automatically set the current step when a component mounts
 */
export function useOnboardingStep(
  step: 1 | 2 | 3 | 4,
  options?: { showProgress?: boolean },
) {
  const { setCurrentStep, setShowProgress } = useOnboardingContext();

  useEffect(() => {
    setCurrentStep(step);
    if (options?.showProgress !== undefined) {
      setShowProgress(options.showProgress);
    }

    // Reset to true on unmount to ensure other pages show progress by default
    return () => {
      if (options?.showProgress !== undefined) {
        setShowProgress(true);
      }
    };
  }, [step, options?.showProgress, setCurrentStep, setShowProgress]);
}
